const { Criteria } = Shopware.Data;

const TYPES = { welcome: 'Willkommensbonus', earn: 'Online-Bestellung', redeem: 'Einlösung', store: 'Filiale', manual: 'Manuell' };
const STATUS = { pending: 'vorgemerkt', booked: 'gebucht', cancelled: 'storniert' };

export default {
    template: `
<sw-page class="riccardo-loyalty-detail">
    <template #smart-bar-header><h2>Kundenkarte {{ account ? formatCard(account.cardNumber) : '' }}</h2></template>
    <template #smart-bar-actions>
        <sw-button :router-link="{ name: 'riccardo.loyalty.list' }">Zurück</sw-button>
        <sw-button-process variant="primary" :is-loading="isLoading" :process-success="isSaveSuccessful" @click="onSave">Speichern</sw-button-process>
    </template>
    <template #content>
        <sw-card-view v-if="account">
            <sw-card title="Karte" :is-loading="isLoading">
                <sw-container columns="repeat(4, 1fr)" gap="0 24px" style="margin-bottom: 24px;">
                    <div><div style="color:#798490;font-size:12px">Punktestand</div><div style="font-size:28px;font-weight:700">{{ account.pointsBalance.toLocaleString('de-DE') }}</div></div>
                    <div><div style="color:#798490;font-size:12px">Vorgemerkt</div><div style="font-size:28px;font-weight:700">{{ account.pointsPending.toLocaleString('de-DE') }}</div></div>
                    <div><div style="color:#798490;font-size:12px">Gesammelt gesamt</div><div style="font-size:28px;font-weight:700">{{ account.pointsEarnedTotal.toLocaleString('de-DE') }}</div></div>
                    <div><div style="color:#798490;font-size:12px">Eingelöst gesamt</div><div style="font-size:28px;font-weight:700">{{ account.pointsRedeemedTotal.toLocaleString('de-DE') }}</div></div>
                </sw-container>
                <sw-switch-field v-model:value="account.active" label="Karte aktiv (gesperrte Karten sammeln und lösen nicht ein)" />
                <sw-entity-single-select v-model:value="account.customerId" entity="customer" label="Kundenkonto" placeholder="Kein Kundenkonto verknüpft" :label-callback="customerLabel" :criteria="customerCriteria" />
                <sw-text-field v-model:value="account.note" label="Notiz" />
            </sw-card>

            <sw-card title="Punkte buchen" :is-loading="isBooking">
                <sw-container columns="1fr 2fr auto" gap="0 16px" align="end">
                    <sw-number-field v-model:value="booking.points" number-type="int" label="Punkte" help-text="Positiv = Gutschrift, negativ = Abzug" />
                    <sw-text-field v-model:value="booking.description" label="Grund" placeholder="z. B. Kulanz, Übernahme alte Karte" />
                    <sw-button variant="primary" :disabled="!booking.points" style="margin-bottom: 22px" @click="onBook">Buchen</sw-button>
                </sw-container>
            </sw-card>

            <sw-card title="Buchungen" :is-loading="isLoading">
                <sw-data-grid v-if="transactions" :data-source="transactions" :columns="txColumns" :show-selection="false" :show-actions="false" :compact-mode="true">
                    <template #column-createdAt="{ item }">{{ formatDate(item.createdAt) }}</template>
                    <template #column-type="{ item }">{{ typeLabel(item.type) }}</template>
                    <template #column-status="{ item }">{{ statusLabel(item.status) }}</template>
                    <template #column-points="{ item }"><strong :style="{ color: item.points < 0 ? '#de294c' : '#1b7f4b', textDecoration: item.status === 'cancelled' ? 'line-through' : 'none' }">{{ item.points > 0 ? '+' : '' }}{{ item.points.toLocaleString('de-DE') }}</strong></template>
                    <template #column-amount="{ item }">{{ item.amount !== null ? item.amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '' }}</template>
                </sw-data-grid>
                <p v-if="transactions && !transactions.length">Noch keine Buchungen.</p>
            </sw-card>
        </sw-card-view>
    </template>
</sw-page>`,

    inject: ['repositoryFactory', 'loginService'],

    mixins: [Shopware.Mixin.getByName('notification')],

    props: {
        accountId: { type: String, required: true },
    },

    data() {
        return { account: null, transactions: null, isLoading: false, isSaveSuccessful: false, isBooking: false, booking: { points: null, description: '' } };
    },

    computed: {
        accountRepository() {
            return this.repositoryFactory.create('riccardo_loyalty_account');
        },
        transactionRepository() {
            return this.repositoryFactory.create('riccardo_loyalty_transaction');
        },
        customerCriteria() {
            const criteria = new Criteria(1, 25);
            criteria.addFilter(Criteria.equals('guest', false));
            return criteria;
        },
        txColumns() {
            return [
                { property: 'createdAt', label: 'Datum' },
                { property: 'type', label: 'Art' },
                { property: 'description', label: 'Beschreibung' },
                { property: 'reference', label: 'Referenz' },
                { property: 'amount', label: 'Betrag', align: 'right' },
                { property: 'status', label: 'Status' },
                { property: 'points', label: 'Punkte', align: 'right' },
            ];
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatCard(number) {
            return (number || '').replace(/(\d{4})(?=\d)/g, '$1 ');
        },
        formatDate(value) {
            return value ? new Date(value).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '';
        },
        typeLabel(type) {
            return TYPES[type] || type;
        },
        statusLabel(status) {
            return STATUS[status] || status;
        },
        customerLabel(customer) {
            return `${customer.firstName} ${customer.lastName} (${customer.email})`;
        },

        load() {
            this.isLoading = true;
            const txCriteria = new Criteria(1, 100);
            txCriteria.addFilter(Criteria.equals('accountId', this.accountId));
            txCriteria.addSorting(Criteria.sort('createdAt', 'DESC'));

            return Promise.all([
                this.accountRepository.get(this.accountId),
                this.transactionRepository.search(txCriteria),
            ]).then(([account, transactions]) => {
                this.account = account;
                this.transactions = transactions;
            }).finally(() => { this.isLoading = false; });
        },

        onSave() {
            this.isLoading = true;
            this.isSaveSuccessful = false;
            return this.accountRepository.save(this.account).then(() => {
                this.isSaveSuccessful = true;
                return this.load();
            }).catch((error) => {
                const code = error?.response?.data?.errors?.[0]?.code || '';
                this.createNotificationError({ message: code.includes('DUPLICATE') || code === '1062' ? 'Dieses Kundenkonto hat bereits eine Karte.' : 'Speichern fehlgeschlagen.' });
            }).finally(() => { this.isLoading = false; });
        },

        onBook() {
            this.isBooking = true;
            const httpClient = Shopware.Application.getContainer('init').httpClient;
            return httpClient.post(`_action/riccardo-loyalty/account/${this.accountId}/booking`, {
                points: this.booking.points,
                description: this.booking.description,
            }, {
                headers: { Authorization: `Bearer ${this.loginService.getToken()}` },
            }).then(() => {
                this.createNotificationSuccess({ message: 'Buchung gespeichert' });
                this.booking = { points: null, description: '' };
                return this.load();
            }).catch((error) => {
                const status = error?.response?.status;
                this.createNotificationError({ message: status === 422 ? 'Nicht genug Punkte für diesen Abzug.' : 'Buchung fehlgeschlagen.' });
            }).finally(() => { this.isBooking = false; });
        },
    },
};
